import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Download,
  Upload,
  FileText,
  Building2,
  Phone,
  Mail,
  MapPin,
  Trash2,
  Filter,
  Eye,
  Edit2,
  CheckCircle,
  XCircle,
  Star,
  X,
} from "lucide-react";
import {
  getSupplierMaster,
  createSupplierMaster,
  updateSupplierMaster,
  deleteSupplierMaster,
} from "../services/api";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import { API_BASE_URL } from "../config/api";

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTier, setSelectedTier] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modal States
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [viewMode, setViewMode] = useState(false);

  // Import States
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [clearBeforeImport, setClearBeforeImport] = useState(false);
  const [activeResultTab, setActiveResultTab] = useState("success");
  const [importProgress, setImportProgress] = useState({
    total: 0,
    current: 0,
    success: 0,
    created: 0,
    updated: 0,
    skipped: 0,
    errors: 0,
    errorDetails: [],
    successDetails: [],
  });
  const fileInputRef = useRef();

  // Form State
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    legalName: "",
    businessType: "",
    industry: "",
    supplierCategory: "",
    supplierTier: "",
    contactPerson: "",
    contactDesignation: "",
    email: "",
    phone: "",
    alternatePhone: "",
    website: "",
    address: "",
    city: "",
    state: "",
    district: "",
    country: "India",
    pinCode: "",
    gstNumber: "",
    panNumber: "",
    cinNumber: "",
    msmeNumber: "",
    bankName: "",
    bankAccountNumber: "",
    bankIfscCode: "",
    bankBranch: "",
    paymentTerms: "",
    creditLimit: "",
    isActive: true,
  });

  // Form Validation Errors
  const [formErrors, setFormErrors] = useState({});

  // Options
  const supplierCategories = [
    "Raw Material",
    "Packaging",
    "Equipment",
    "Service",
    "Logistics",
    "Technology",
    "Consulting",
    "Maintenance",
    "Other",
  ];

  const supplierTiers = [
    "Tier 1",
    "Tier 2",
    "Tier 3",
    "Preferred",
    "Approved",
    "Strategic",
    "Development",
  ];

  const businessTypes = [
    "Proprietorship",
    "Partnership",
    "Private Limited",
    "Public Limited",
    "LLP",
    "Sole Proprietorship",
  ];

  const industries = [
    "Manufacturing",
    "Trading",
    "Service",
    "Retail",
    "Wholesale",
    "Import/Export",
    "Technology",
    "Healthcare",
    "Automotive",
    "Construction",
    "Food & Beverage",
    "Textile",
    "Electronics",
    "Chemicals",
    "Other",
  ];

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await getSupplierMaster({});
      setSuppliers(response.suppliers || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast.error("Failed to fetch suppliers");
    } finally {
      setLoading(false);
    }
  };

  // Stats calculation
  const stats = useMemo(
    () => ({
      total: suppliers.length,
      active: suppliers.filter((s) => s.isActive).length,
      inactive: suppliers.filter((s) => !s.isActive).length,
      verified: suppliers.filter((s) => s.isVerified).length,
    }),
    [suppliers],
  );

  // Filter suppliers
  const filteredSuppliers = useMemo(() => {
    return suppliers.filter((supplier) => {
      const matchesSearch =
        !searchTerm ||
        supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.phone?.includes(searchTerm);

      const matchesCategory =
        !selectedCategory || supplier.supplierCategory === selectedCategory;

      const matchesTier =
        !selectedTier || supplier.supplierTier === selectedTier;

      const matchesStatus =
        !selectedStatus ||
        (selectedStatus === "active" && supplier.isActive) ||
        (selectedStatus === "inactive" && !supplier.isActive) ||
        (selectedStatus === "verified" && supplier.isVerified);

      return matchesSearch && matchesCategory && matchesTier && matchesStatus;
    });
  }, [suppliers, searchTerm, selectedCategory, selectedTier, selectedStatus]);

  // Pagination
  const totalItems = filteredSuppliers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedSuppliers = filteredSuppliers.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedTier, selectedStatus]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      legalName: "",
      businessType: "",
      industry: "",
      supplierCategory: "",
      supplierTier: "",
      contactPerson: "",
      contactDesignation: "",
      email: "",
      phone: "",
      alternatePhone: "",
      website: "",
      address: "",
      city: "",
      state: "",
      district: "",
      country: "India",
      pinCode: "",
      gstNumber: "",
      panNumber: "",
      cinNumber: "",
      msmeNumber: "",
      bankName: "",
      bankAccountNumber: "",
      bankIfscCode: "",
      bankBranch: "",
      paymentTerms: "",
      creditLimit: "",
      isActive: true,
    });
    setEditingId(null);
    setFormErrors({});
    setViewMode(false);
    setSelectedSupplier(null);
  };

  const handleAddSupplier = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (supplier) => {
    setFormData({
      code: supplier.code || "",
      name: supplier.name || "",
      legalName: supplier.legalName || "",
      businessType: supplier.businessType || "",
      industry: supplier.industry || "",
      supplierCategory: supplier.supplierCategory || "",
      supplierTier: supplier.supplierTier || "",
      contactPerson: supplier.contactPerson || "",
      contactDesignation: supplier.contactDesignation || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      alternatePhone: supplier.alternatePhone || "",
      website: supplier.website || "",
      address: supplier.address || "",
      city: supplier.city || "",
      state: supplier.state || "",
      district: supplier.district || "",
      country: supplier.country || "India",
      pinCode: supplier.pinCode || "",
      gstNumber: supplier.gstNumber || "",
      panNumber: supplier.panNumber || "",
      cinNumber: supplier.cinNumber || "",
      msmeNumber: supplier.msmeNumber || "",
      bankName: supplier.bankName || "",
      bankAccountNumber: supplier.bankAccountNumber || "",
      bankIfscCode: supplier.bankIfscCode || "",
      bankBranch: supplier.bankBranch || "",
      paymentTerms: supplier.paymentTerms || "",
      creditLimit: supplier.creditLimit || "",
      isActive: supplier.isActive !== undefined ? supplier.isActive : true,
    });
    setEditingId(supplier.id);
    setShowAddModal(true);
  };

  const handleView = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      code: supplier.code || "",
      name: supplier.name || "",
      legalName: supplier.legalName || "",
      businessType: supplier.businessType || "",
      industry: supplier.industry || "",
      supplierCategory: supplier.supplierCategory || "",
      supplierTier: supplier.supplierTier || "",
      contactPerson: supplier.contactPerson || "",
      contactDesignation: supplier.contactDesignation || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      alternatePhone: supplier.alternatePhone || "",
      website: supplier.website || "",
      address: supplier.address || "",
      city: supplier.city || "",
      state: supplier.state || "",
      district: supplier.district || "",
      country: supplier.country || "India",
      pinCode: supplier.pinCode || "",
      gstNumber: supplier.gstNumber || "",
      panNumber: supplier.panNumber || "",
      cinNumber: supplier.cinNumber || "",
      msmeNumber: supplier.msmeNumber || "",
      bankName: supplier.bankName || "",
      bankAccountNumber: supplier.bankAccountNumber || "",
      bankIfscCode: supplier.bankIfscCode || "",
      bankBranch: supplier.bankBranch || "",
      paymentTerms: supplier.paymentTerms || "",
      creditLimit: supplier.creditLimit || "",
      isActive: supplier.isActive !== undefined ? supplier.isActive : true,
    });
    setViewMode(true);
    setShowAddModal(true);
  };

  // Form Validation
  const validateForm = () => {
    const errors = {};

    // Required field validations
    if (!formData.name.trim()) {
      errors.name = "Supplier name is required";
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Phone validation (Indian format - 10 digits)
    if (
      formData.phone &&
      !/^[\d\s+()-]{10,15}$/.test(formData.phone.replace(/\s/g, ""))
    ) {
      errors.phone = "Please enter a valid phone number";
    }

    // Alternate phone validation
    if (
      formData.alternatePhone &&
      !/^[\d\s+()-]{10,15}$/.test(formData.alternatePhone.replace(/\s/g, ""))
    ) {
      errors.alternatePhone = "Please enter a valid phone number";
    }

    // GST Number validation (15 characters alphanumeric)
    if (
      formData.gstNumber &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        formData.gstNumber.toUpperCase(),
      )
    ) {
      errors.gstNumber =
        "Please enter a valid GST number (e.g., 22AAAAA0000A1Z5)";
    }

    // PAN Number validation (10 characters - AAAAA0000A)
    if (
      formData.panNumber &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber.toUpperCase())
    ) {
      errors.panNumber = "Please enter a valid PAN number (e.g., AAAAA0000A)";
    }

    // Pin code validation (6 digits for India)
    if (formData.pinCode && !/^[1-9][0-9]{5}$/.test(formData.pinCode)) {
      errors.pinCode = "Please enter a valid 6-digit pin code";
    }

    // IFSC Code validation (11 characters - first 4 alpha, 5th is 0, last 6 alphanumeric)
    if (
      formData.bankIfscCode &&
      !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.bankIfscCode.toUpperCase())
    ) {
      errors.bankIfscCode =
        "Please enter a valid IFSC code (e.g., SBIN0001234)";
    }

    // Credit limit validation (should be a positive number)
    if (
      formData.creditLimit &&
      (isNaN(formData.creditLimit) || Number(formData.creditLimit) < 0)
    ) {
      errors.creditLimit = "Credit limit must be a positive number";
    }

    // Website validation
    if (
      formData.website &&
      !/^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/.*)?$/.test(
        formData.website,
      )
    ) {
      errors.website = "Please enter a valid website URL";
    }

    // CIN validation (21 characters)
    if (
      formData.cinNumber &&
      !/^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(
        formData.cinNumber.toUpperCase(),
      )
    ) {
      errors.cinNumber = "Please enter a valid CIN number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    try {
      setLoading(true);
      if (editingId) {
        await updateSupplierMaster(editingId, formData);
        toast.success("Supplier updated successfully");
      } else {
        await createSupplierMaster(formData);
        toast.success("Supplier created successfully");
      }
      setShowAddModal(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      console.error("Error saving supplier:", error);
      toast.error(error.message || "Failed to save supplier");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (supplier) => {
    setSelectedSupplier(supplier);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSupplier) return;

    try {
      setLoading(true);
      await deleteSupplierMaster(selectedSupplier.id);
      toast.success("Supplier deleted successfully");
      setShowDeleteModal(false);
      setSelectedSupplier(null);
      fetchSuppliers();
    } catch (error) {
      console.error("Error deleting supplier:", error);
      toast.error(error.message || "Failed to delete supplier");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const exportData = filteredSuppliers.map((supplier) => ({
      Code: supplier.code || "",
      Name: supplier.name || "",
      "Legal Name": supplier.legalName || "",
      Category: supplier.supplierCategory || "",
      Tier: supplier.supplierTier || "",
      "Contact Person": supplier.contactPerson || "",
      Email: supplier.email || "",
      Phone: supplier.phone || "",
      Address: supplier.address || "",
      City: supplier.city || "",
      State: supplier.state || "",
      Country: supplier.country || "",
      "Pin Code": supplier.pinCode || "",
      "GST Number": supplier.gstNumber || "",
      "PAN Number": supplier.panNumber || "",
      "Payment Terms": supplier.paymentTerms || "",
      "Credit Limit": supplier.creditLimit || "",
      Status: supplier.isActive ? "Active" : "Inactive",
      Verified: supplier.isVerified ? "Yes" : "No",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Suppliers");
    XLSX.writeFile(
      wb,
      `suppliers_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
    toast.success("Suppliers exported successfully");
  };

  // Download template function
  const downloadTemplate = async () => {
    try {
      // Create a sample template with supplier fields
      const templateData = [
        {
          "Code*": "SUP-001",
          "Name*": "Sample Supplier Ltd",
          "Legal Name": "Sample Supplier Private Limited",
          Category: "Raw Materials",
          Tier: "Tier 1",
          "Contact Person*": "John Doe",
          Email: "john@samplesupplier.com",
          "Phone*": "+91 9876543210",
          Address: "123 Business Park",
          City: "Mumbai",
          State: "Maharashtra",
          Country: "India",
          "Pin Code": "400001",
          "GST Number": "27XXXXX1234X1Z5",
          "PAN Number": "XXXXX1234X",
          "Payment Terms": "Net 30",
          "Credit Limit": "100000",
          Status: "Active",
          Verified: "Yes",
        },
        {
          "Code*": "SUP-002",
          "Name*": "Another Supplier Corp",
          "Legal Name": "Another Supplier Corporation",
          Category: "Packaging",
          Tier: "Tier 2",
          "Contact Person*": "Jane Smith",
          Email: "jane@anothersupplier.com",
          "Phone*": "+91 9876543211",
          Address: "456 Industrial Area",
          City: "Delhi",
          State: "Delhi",
          Country: "India",
          "Pin Code": "110001",
          "GST Number": "07XXXXX5678X1Z9",
          "PAN Number": "XXXXX5678X",
          "Payment Terms": "Net 45",
          "Credit Limit": "150000",
          Status: "Active",
          Verified: "No",
        },
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Suppliers Template");

      // Add instructions sheet
      const instructions = [
        {
          Field: "Code*",
          Required: "Yes",
          Description: "Unique supplier code (e.g., SUP-001)",
        },
        {
          Field: "Name*",
          Required: "Yes",
          Description: "Supplier business name",
        },
        {
          Field: "Legal Name",
          Required: "No",
          Description: "Legal registered name of the company",
        },
        {
          Field: "Category",
          Required: "No",
          Description:
            "Supplier category (Raw Materials, Packaging, Services, etc.)",
        },
        {
          Field: "Tier",
          Required: "No",
          Description: "Supplier tier (Tier 1, Tier 2, Tier 3)",
        },
        {
          Field: "Contact Person*",
          Required: "Yes",
          Description: "Primary contact person name",
        },
        {
          Field: "Email",
          Required: "No",
          Description: "Contact email address",
        },
        {
          Field: "Phone*",
          Required: "Yes",
          Description: "Contact phone number",
        },
        {
          Field: "Address",
          Required: "No",
          Description: "Business address",
        },
        {
          Field: "Status",
          Required: "No",
          Description: "Active or Inactive",
        },
        {
          Field: "Verified",
          Required: "No",
          Description: "Yes or No",
        },
      ];

      const wsInstructions = XLSX.utils.json_to_sheet(instructions);
      XLSX.utils.book_append_sheet(wb, wsInstructions, "Instructions");

      XLSX.writeFile(wb, "suppliers_import_template.xlsx");
      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Template download error:", error);
      toast.error("Failed to download template");
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel"
      ) {
        setImportFile(file);
      } else {
        toast.error("Please select a valid Excel file (.xlsx or .xls)");
      }
    }
  };

  // Handle import
  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }

    setImporting(true);
    setImportProgress({
      total: 0,
      current: 0,
      success: 0,
      created: 0,
      updated: 0,
      skipped: 0,
      errors: 0,
      errorDetails: [],
      successDetails: [],
    });

    try {
      const data = await importFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: "buffer" });
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        toast.error("No data found in the Excel file");
        setImporting(false);
        return;
      }

      setImportProgress((prev) => ({ ...prev, total: jsonData.length }));

      let created = 0,
        updated = 0,
        skipped = 0,
        errors = 0;
      const errorDetails = [];
      const successDetails = [];

      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        setImportProgress((prev) => ({ ...prev, current: i + 1 }));

        try {
          // Validate required fields
          if (
            !row["Code*"] ||
            !row["Name*"] ||
            !row["Contact Person*"] ||
            !row["Phone*"]
          ) {
            errors++;
            errorDetails.push({
              row: i + 2,
              code: row["Code*"] || "N/A",
              name: row["Name*"] || "N/A",
              error:
                "Missing required fields (Code, Name, Contact Person, Phone)",
            });
            continue;
          }

          // Check if supplier exists
          const existingSupplier = suppliers.find(
            (s) => s.code === row["Code*"],
          );

          const supplierData = {
            code: row["Code*"],
            name: row["Name*"],
            legalName: row["Legal Name"] || "",
            supplierCategory: row["Category"] || "",
            supplierTier: row["Tier"] || "",
            contactPerson: row["Contact Person*"],
            email: row["Email"] || "",
            phone: row["Phone*"],
            address: row["Address"] || "",
            city: row["City"] || "",
            state: row["State"] || "",
            country: row["Country"] || "",
            pinCode: row["Pin Code"] || "",
            gstNumber: row["GST Number"] || "",
            panNumber: row["PAN Number"] || "",
            paymentTerms: row["Payment Terms"] || "",
            creditLimit: row["Credit Limit"]
              ? parseFloat(row["Credit Limit"])
              : null,
            isActive: row["Status"] !== "Inactive",
            isVerified: row["Verified"] === "Yes",
          };

          if (existingSupplier) {
            if (replaceExisting) {
              await updateSupplierMaster(existingSupplier.id, supplierData);
              updated++;
              successDetails.push({
                row: i + 2,
                code: row["Code*"],
                name: row["Name*"],
                action: "Updated",
              });
            } else {
              skipped++;
              successDetails.push({
                row: i + 2,
                code: row["Code*"],
                name: row["Name*"],
                action: "Skipped (exists)",
              });
            }
          } else {
            await createSupplierMaster(supplierData);
            created++;
            successDetails.push({
              row: i + 2,
              code: row["Code*"],
              name: row["Name*"],
              action: "Created",
            });
          }
        } catch (error) {
          errors++;
          errorDetails.push({
            row: i + 2,
            code: row["Code*"] || "N/A",
            name: row["Name*"] || "N/A",
            error: error.message || "Failed to process supplier",
          });
        }
      }

      setImportProgress({
        total: jsonData.length,
        current: jsonData.length,
        success: created + updated,
        created,
        updated,
        skipped,
        errors,
        errorDetails,
        successDetails,
      });

      setActiveResultTab(errors > 0 ? "errors" : "success");

      // Refresh suppliers list
      await fetchSuppliers();

      if (errors === 0) {
        toast.success(
          `Import completed successfully! ${created} created, ${updated} updated`,
        );
      } else {
        toast(
          `Import completed with ${errors} errors. ${created} created, ${updated} updated`,
        );
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to process import file");
      setImportProgress({
        total: 0,
        current: 0,
        success: 0,
        created: 0,
        updated: 0,
        skipped: 0,
        errors: 1,
        errorDetails: [
          {
            row: 0,
            code: "N/A",
            name: "N/A",
            error: "Failed to process file",
          },
        ],
        successDetails: [],
      });
      setActiveResultTab("errors");
    } finally {
      setImporting(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setSelectedTier("");
    setSelectedStatus("");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    searchTerm || selectedCategory || selectedTier || selectedStatus;

  const getStatusBadge = (isActive, isVerified) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">
          <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-emerald-500" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800">
        <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-red-500" />
        Inactive
      </span>
    );
  };

  if (loading && suppliers.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Suppliers
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your supplier database
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={handleAddSupplier}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 transition-all duration-200"
          >
            <Plus className="w-4 h-4" />
            Add Supplier
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.total}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Suppliers
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.active}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.inactive}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Inactive
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <Star className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.verified}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Verified
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-all ${
                showFilters || hasActiveFilters
                  ? "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300"
                  : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                  {
                    [selectedCategory, selectedTier, selectedStatus].filter(
                      Boolean,
                    ).length
                  }
                </span>
              )}
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="">All Categories</option>
                  {supplierCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  Tier
                </label>
                <select
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="">All Tiers</option>
                  {supplierTiers.map((tier) => (
                    <option key={tier} value={tier}>
                      {tier}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="verified">Verified</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {indexOfFirstItem + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {Math.min(indexOfLastItem, totalItems)}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {totalItems}
            </span>{" "}
            suppliers
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900/50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">
                  Location
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {paginatedSuppliers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <Building2 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                      No suppliers found
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                      Try adjusting your search or filters
                    </p>
                  </td>
                </tr>
              ) : (
                paginatedSuppliers.map((supplier) => (
                  <tr
                    key={supplier.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        {supplier.code || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                          {supplier.name?.charAt(0)?.toUpperCase() || "S"}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {supplier.name || "-"}
                          </p>
                          {supplier.legalName && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {supplier.legalName}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="space-y-1">
                        {supplier.contactPerson && (
                          <p className="text-sm text-gray-900 dark:text-white">
                            {supplier.contactPerson}
                          </p>
                        )}
                        {supplier.email && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {supplier.email}
                          </p>
                        )}
                        {supplier.phone && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {supplier.phone}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div>
                        {supplier.supplierCategory && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {supplier.supplierCategory}
                          </p>
                        )}
                        {supplier.supplierTier && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 rounded">
                            {supplier.supplierTier}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[supplier.city, supplier.state]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {getStatusBadge(supplier.isActive, supplier.isVerified)}
                        {supplier.isVerified && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            <Star className="w-3 h-3 mr-1" />
                            Verified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleView(supplier)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(supplier)}
                          className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                          title="Edit Supplier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(supplier)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Delete Supplier"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === 1
                    ? "text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed"
                    : "text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (num) =>
                    num === 1 ||
                    num === totalPages ||
                    (num >= currentPage - 1 && num <= currentPage + 1),
                )
                .reduce((acc, num, i, arr) => {
                  if (i > 0 && num - arr[i - 1] > 1) {
                    acc.push("...");
                  }
                  acc.push(num);
                  return acc;
                }, [])
                .map((item, index) =>
                  item === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 text-gray-400"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => paginate(item)}
                      className={`min-w-[36px] h-9 rounded-lg border text-sm font-medium transition-colors ${
                        currentPage === item
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                    >
                      {item}
                    </button>
                  ),
                )}

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === totalPages
                    ? "text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed"
                    : "text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit/View Supplier Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl">
            {/* Fixed Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {viewMode
                  ? "View Supplier"
                  : editingId
                    ? "Edit Supplier"
                    : "Add New Supplier"}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Form Content */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col flex-1 overflow-hidden"
            >
              <div className="p-6 overflow-y-auto flex-1">
                <div className="grid grid-cols-2 gap-4">
                  {/* Basic Info */}
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 pb-2 border-b">
                      Basic Information
                    </h4>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supplier Code
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="Auto-generated if empty"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supplier Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.name
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="Company name"
                    />
                    {formErrors.name && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Legal Name
                    </label>
                    <input
                      type="text"
                      name="legalName"
                      value={formData.legalName}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="Registered legal name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Type
                    </label>
                    <select
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleInputChange}
                      disabled={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    >
                      <option value="">Select Type</option>
                      {businessTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industry
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      disabled={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    >
                      <option value="">Select Industry</option>
                      {industries.map((ind) => (
                        <option key={ind} value={ind}>
                          {ind}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      name="supplierCategory"
                      value={formData.supplierCategory}
                      onChange={handleInputChange}
                      disabled={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    >
                      <option value="">Select Category</option>
                      {supplierCategories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tier
                    </label>
                    <select
                      name="supplierTier"
                      value={formData.supplierTier}
                      onChange={handleInputChange}
                      disabled={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    >
                      <option value="">Select Tier</option>
                      {supplierTiers.map((tier) => (
                        <option key={tier} value={tier}>
                          {tier}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Contact Info */}
                  <div className="col-span-2 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 pb-2 border-b">
                      Contact Information
                    </h4>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Person
                    </label>
                    <input
                      type="text"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="Primary contact name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Designation
                    </label>
                    <input
                      type="text"
                      name="contactDesignation"
                      value={formData.contactDesignation}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="e.g., Manager, Director"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="text"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.email
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="email@company.com"
                    />
                    {formErrors.email && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.phone
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="+91 9876543210"
                    />
                    {formErrors.phone && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Alternate Phone
                    </label>
                    <input
                      type="text"
                      name="alternatePhone"
                      value={formData.alternatePhone}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.alternatePhone
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="Alternate phone number"
                    />
                    {formErrors.alternatePhone && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.alternatePhone}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.website
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="www.company.com"
                    />
                    {formErrors.website && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.website}
                      </p>
                    )}
                  </div>

                  {/* Address */}
                  <div className="col-span-2 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 pb-2 border-b">
                      Address
                    </h4>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      District
                    </label>
                    <input
                      type="text"
                      name="district"
                      value={formData.district}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="District"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="Country"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pin Code
                    </label>
                    <input
                      type="text"
                      name="pinCode"
                      value={formData.pinCode}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.pinCode
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="Pin code"
                    />
                    {formErrors.pinCode && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.pinCode}
                      </p>
                    )}
                  </div>

                  {/* Tax & Financial Info */}
                  <div className="col-span-2 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 pb-2 border-b">
                      Tax & Registration Information
                    </h4>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GST Number
                    </label>
                    <input
                      type="text"
                      name="gstNumber"
                      value={formData.gstNumber}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.gstNumber
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="GSTIN"
                    />
                    {formErrors.gstNumber && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.gstNumber}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PAN Number
                    </label>
                    <input
                      type="text"
                      name="panNumber"
                      value={formData.panNumber}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.panNumber
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="PAN"
                    />
                    {formErrors.panNumber && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.panNumber}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CIN Number
                    </label>
                    <input
                      type="text"
                      name="cinNumber"
                      value={formData.cinNumber}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.cinNumber
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="Corporate Identification Number"
                    />
                    {formErrors.cinNumber && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.cinNumber}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      MSME Number
                    </label>
                    <input
                      type="text"
                      name="msmeNumber"
                      value={formData.msmeNumber}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="MSME Registration Number"
                    />
                  </div>

                  {/* Bank Details */}
                  <div className="col-span-2 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 pb-2 border-b">
                      Bank Details
                    </h4>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="Bank Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="bankAccountNumber"
                      value={formData.bankAccountNumber}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="Account Number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IFSC Code
                    </label>
                    <input
                      type="text"
                      name="bankIfscCode"
                      value={formData.bankIfscCode}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.bankIfscCode
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="IFSC Code"
                    />
                    {formErrors.bankIfscCode && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.bankIfscCode}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Branch
                    </label>
                    <input
                      type="text"
                      name="bankBranch"
                      value={formData.bankBranch}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="Branch Name"
                    />
                  </div>

                  {/* Payment Terms */}
                  <div className="col-span-2 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 pb-2 border-b">
                      Payment Information
                    </h4>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Terms
                    </label>
                    <input
                      type="text"
                      name="paymentTerms"
                      value={formData.paymentTerms}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="e.g., Net 30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Credit Limit
                    </label>
                    <input
                      type="number"
                      name="creditLimit"
                      value={formData.creditLimit}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        viewMode
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : formErrors.creditLimit
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                      }`}
                      placeholder="0.00"
                    />
                    {formErrors.creditLimit && !viewMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.creditLimit}
                      </p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="col-span-2 mt-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        disabled={viewMode}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-60"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Active Supplier
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Fixed Footer */}
              <div className="p-6 border-t border-gray-200 flex-shrink-0 flex gap-3 justify-end bg-gray-50">
                {viewMode ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading
                        ? "Saving..."
                        : editingId
                          ? "Update Supplier"
                          : "Create Supplier"}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6 shadow-xl">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Delete Supplier
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {selectedSupplier?.name}
                </span>
                ? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedSupplier(null);
                  }}
                  className="px-5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={loading}
                  className="px-5 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Import Suppliers
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Upload Excel file to import supplier data
                </p>
              </div>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImporting(false);
                  setImportProgress({
                    total: 0,
                    current: 0,
                    success: 0,
                    created: 0,
                    updated: 0,
                    skipped: 0,
                    errors: 0,
                    errorDetails: [],
                    successDetails: [],
                  });
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Step Indicators */}
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center space-x-4">
                  {/* Step 1 */}
                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium">
                      1
                    </div>
                    <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                      Download Template
                    </span>
                  </div>
                  <div className="w-12 h-px bg-gray-300 dark:bg-gray-600"></div>

                  {/* Step 2 */}
                  <div className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                        importFile
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      2
                    </div>
                    <span
                      className={`ml-2 text-sm font-medium ${
                        importFile
                          ? "text-gray-900 dark:text-white"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      Upload File
                    </span>
                  </div>
                  <div className="w-12 h-px bg-gray-300 dark:bg-gray-600"></div>

                  {/* Step 3 */}
                  <div className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                        importing || importProgress.total > 0
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      3
                    </div>
                    <span
                      className={`ml-2 text-sm font-medium ${
                        importing || importProgress.total > 0
                          ? "text-gray-900 dark:text-white"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      Import Data
                    </span>
                  </div>
                </div>
              </div>

              {/* Step 1: Download Template */}
              <div className="mb-6">
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 dark:text-blue-400 mb-1">
                        Step 1: Download Template
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                        Download the Excel template with sample data and field
                        descriptions. Fill in your supplier data and save as
                        Excel file.
                      </p>
                      <button
                        onClick={downloadTemplate}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download Template
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2: File Upload */}
              <div className="mb-6">
                <div
                  className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
                    importFile
                      ? "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/30"
                      : "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30"
                  }`}
                >
                  <div className="text-center">
                    <Upload
                      className={`mx-auto h-12 w-12 ${
                        importFile
                          ? "text-green-600 dark:text-green-400"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                    />
                    <h4
                      className={`mt-2 font-semibold ${
                        importFile
                          ? "text-green-900 dark:text-green-400"
                          : "text-gray-900 dark:text-white"
                      }`}
                    >
                      Step 2: Upload Excel File
                    </h4>
                    {importFile ? (
                      <div className="mt-2">
                        <p className="text-sm text-green-700 dark:text-green-300">
                          File selected: {importFile.name}
                        </p>
                        <button
                          onClick={() => {
                            setImportFile(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          className="mt-2 text-sm text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                          Select your Excel file with supplier data
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
                        >
                          <Upload className="w-4 h-4" />
                          Choose File
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Step 3: Import Options */}
              {importFile && (
                <div className="mb-6">
                  <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                      Step 3: Import Options
                    </h4>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={replaceExisting}
                          onChange={(e) => setReplaceExisting(e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Replace existing suppliers (matching by code)
                        </span>
                      </label>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={clearBeforeImport}
                          onChange={(e) =>
                            setClearBeforeImport(e.target.checked)
                          }
                          className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Clear existing data before import
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Import Progress */}
              {importing && (
                <div className="mb-6">
                  <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 dark:text-blue-400 mb-3">
                      Processing Import...
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300">
                        <span>
                          Progress: {importProgress.current} /{" "}
                          {importProgress.total}
                        </span>
                        <span>
                          {Math.round(
                            (importProgress.current / importProgress.total) *
                              100,
                          )}
                          %
                        </span>
                      </div>
                      <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${(importProgress.current / importProgress.total) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Import Results */}
              {importProgress.total > 0 && !importing && (
                <div className="mb-6">
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        Import Results
                      </h4>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span className="text-green-600 dark:text-green-400">
                          ✓ {importProgress.created} created
                        </span>
                        <span className="text-blue-600 dark:text-blue-400">
                          ↻ {importProgress.updated} updated
                        </span>
                        <span className="text-yellow-600 dark:text-yellow-400">
                          ⊘ {importProgress.skipped} skipped
                        </span>
                        <span className="text-red-600 dark:text-red-400">
                          ✗ {importProgress.errors} errors
                        </span>
                      </div>
                    </div>

                    {/* Result tabs */}
                    <div className="flex border-b border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setActiveResultTab("success")}
                        className={`px-4 py-2 text-sm font-medium border-b-2 ${
                          activeResultTab === "success"
                            ? "border-blue-500 text-blue-600 dark:text-blue-400"
                            : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                      >
                        Success ({importProgress.successDetails.length})
                      </button>
                      <button
                        onClick={() => setActiveResultTab("errors")}
                        className={`px-4 py-2 text-sm font-medium border-b-2 ${
                          activeResultTab === "errors"
                            ? "border-blue-500 text-blue-600 dark:text-blue-400"
                            : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                      >
                        Errors ({importProgress.errorDetails.length})
                      </button>
                    </div>

                    <div className="p-4 max-h-64 overflow-y-auto">
                      {activeResultTab === "success" && (
                        <div className="space-y-2">
                          {importProgress.successDetails.length > 0 ? (
                            importProgress.successDetails.map((item, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-3 p-2 bg-green-50 dark:bg-green-900/30 rounded border border-green-200 dark:border-green-800"
                              >
                                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                                <span className="text-sm text-green-800 dark:text-green-300">
                                  Row {item.row}: {item.name} ({item.code}) -{" "}
                                  {item.action}
                                </span>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              No successful imports
                            </p>
                          )}
                        </div>
                      )}

                      {activeResultTab === "errors" && (
                        <div className="space-y-2">
                          {importProgress.errorDetails.length > 0 ? (
                            importProgress.errorDetails.map((item, index) => (
                              <div
                                key={index}
                                className="flex items-start gap-3 p-2 bg-red-50 dark:bg-red-900/30 rounded border border-red-200 dark:border-red-800"
                              >
                                <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5" />
                                <div className="text-sm text-red-800 dark:text-red-300">
                                  <div className="font-medium">
                                    Row {item.row}: {item.name} ({item.code})
                                  </div>
                                  <div className="text-red-600 dark:text-red-400">
                                    {item.error}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              No errors
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setImporting(false);
                    setImportProgress({
                      total: 0,
                      current: 0,
                      success: 0,
                      created: 0,
                      updated: 0,
                      skipped: 0,
                      errors: 0,
                      errorDetails: [],
                      successDetails: [],
                    });
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  disabled={importing}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                {importFile && (
                  <button
                    onClick={handleImport}
                    disabled={importing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {importing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Import Suppliers
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
